import { useCallback } from 'react';
import PropTypes from 'prop-types';
import NiceModal from '@ebay/nice-modal-react';
import Button from '@mui/material/Button';
import { isEmpty } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import { ComponentLoader } from 'components';
import Sidebar from 'components/atoms/GefMui/Sidebar/Sidebar';
import MODALS from 'constants/modals';
import fetch from 'features/common/fetch';
import {
	addContactDataColumnWithMaxItemsCheck,
	fetchCampaignDetail,
	removeContactDataColumn,
	resetCampaignSettings,
} from 'features/views/campaignSettings/campaignDetail/actions';
import { checkIfCampaignStatusNew, getDetail } from 'features/views/campaignSettings/campaignDetail/selectors';
import { templateTypes } from 'features/views/campaignSettings/templates/constants';

import SidebarDragContainer from './SidebarDragContainer';

export const CampaignSidebar = ({ campaignId }) => {
	const dispatch = useDispatch();

	const campaignDetail = useSelector(getDetail);
	const isStatusNew = useSelector(checkIfCampaignStatusNew);

	const handleItemClick = useCallback(
		(id) => {
			dispatch(addContactDataColumnWithMaxItemsCheck(id));
		},
		[dispatch],
	);

	const handleClearSetup = useCallback(() => {
		NiceModal.show(MODALS.lightbox_modal, {
			title: getLabelMessage('lightBox:campaignSettingsClear'),
			onConfirm: () => {
				dispatch(resetCampaignSettings(campaignId));
			},
		});
	}, [dispatch, campaignId]);

	const handleOnDrop = useCallback(
		(id) => {
			dispatch(removeContactDataColumn(id));
		},
		[dispatch],
	);

	const handleTemplateApplied = useCallback(() => {
		dispatch(fetchCampaignDetail(campaignId));
	}, [dispatch, campaignId]);

	const handleLoadFromTemplate = useCallback(
		() =>
			NiceModal.show(MODALS.campaignSettings_templates, {
				type: templateTypes.LOAD,
				campaignDetail,
				onSuccess: handleTemplateApplied,
			}),
		[campaignDetail, handleTemplateApplied],
	);

	const handleSaveTemplate = useCallback(() => {
		NiceModal.show(MODALS.campaignSettings_templates, {
			type: templateTypes.SAVE,
			campaignDetail,
		});
	}, [campaignDetail]);

	return (
		<Sidebar title={getLabelMessage('campaign:sidebarTitle')}>
			{!isEmpty(campaignDetail) && (
				<>
					<ComponentLoader fetchStorePath={fetch.constants.urls.campaignStatus}>
						<Sidebar.Body>
							<SidebarDragContainer onDrop={handleOnDrop} onClick={handleItemClick} />
						</Sidebar.Body>
					</ComponentLoader>
					<Sidebar.Footer>
						<Button onClick={handleLoadFromTemplate} fullWidth>
							{getLabelMessage('sidebar:loadFromTempalte')}
						</Button>
						<Button onClick={handleSaveTemplate} fullWidth>
							{getLabelMessage('sidebar:saveTemplate')}
						</Button>
						<Button onClick={handleClearSetup} fullWidth disabled={!isStatusNew}>
							{getLabelMessage('sidebar:deleteConfiguration')}
						</Button>
					</Sidebar.Footer>
				</>
			)}
		</Sidebar>
	);
};

CampaignSidebar.propTypes = {
	campaignDataColumns: PropTypes.array,
	campaignId: PropTypes.number,
};

export default CampaignSidebar;
